import {
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import {
  fetchOrganizations,
  fetchOrganization,
  createOrganization,
  updateOrganizationMemorySchemaConfig,
} from "../actions/organizationActions";

// Organization interfaces
export interface APIOrganization {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  is_active?: boolean;
  is_prod_release?: boolean;
  requires_redaction?: boolean;
  active_deployment?: {
    id: string;
    created_at: string;
    build: {
      id: string;
      build_name: string;
      entries: Array<{
        platform: string;
        repository_type: string;
        key: string;
      }>;
      deleted_at: string | null;
    };
    is_testbed_deployment: boolean;
    min_hosts: number;
    max_hosts: number | null;
  };
  session_launch_policy?: {
    allow_session_launch: boolean;
    supported_login_methods: string[];
  };
  memory_schema_config?: {
    restrict_memory_schemas: boolean;
    allowed_schemas: string[];
  };
}

// State interface
interface OrganizationState {
  organizations: APIOrganization[];
  currentOrganization: APIOrganization | null;
  loading: boolean;
  error: string | null;
  memoryConfigError: string | null;
}

// Initial state
const initialState: OrganizationState = {
  organizations: [],
  currentOrganization: null,
  loading: false,
  error: null,
  memoryConfigError: null,
};

// Create the slice
const organizationSlice = createSlice({
  name: "organizations",
  initialState,
  reducers: {
    clearCurrentOrganization(state) {
      state.currentOrganization = null;
    },
    clearMemoryConfigError(state) {
      state.memoryConfigError = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<OrganizationState>) => {
    // Handle fetchOrganizations
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchOrganizations.fulfilled,
        (state, action: PayloadAction<APIOrganization[]>) => {
          state.loading = false;
          state.organizations = action.payload;
        }
      )
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch organizations";
      });

    // Handle fetchOrganization
    builder
      .addCase(fetchOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchOrganization.fulfilled,
        (state, action: PayloadAction<APIOrganization>) => {
          state.loading = false;
          state.currentOrganization = action.payload;
        }
      )
      .addCase(fetchOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch organization";
      });

    // Handle createOrganization
    builder
      .addCase(createOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createOrganization.fulfilled,
        (state, action: PayloadAction<APIOrganization>) => {
          state.loading = false;
          state.organizations.push(action.payload);
          state.currentOrganization = action.payload;
        }
      )
      .addCase(createOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create organization";
      });

    // Handle updateOrganizationMemorySchemaConfig
    builder
      .addCase(updateOrganizationMemorySchemaConfig.pending, (state) => {
        state.loading = true;
        state.memoryConfigError = null;
      })
      .addCase(
        updateOrganizationMemorySchemaConfig.fulfilled,
        (state, action) => {
          state.loading = false;
          state.memoryConfigError = null;
          const { organizationId, memory_schema_config } = action.payload;

          // Update currentOrganization if it matches
          if (
            state.currentOrganization &&
            state.currentOrganization.id === organizationId
          ) {
            state.currentOrganization.memory_schema_config =
              memory_schema_config;
          }

          // Update in the organizations array
          const index = state.organizations.findIndex(
            (org) => org.id === organizationId
          );
          if (index !== -1) {
            state.organizations[index].memory_schema_config =
              memory_schema_config;
          }
        }
      )
      .addCase(
        updateOrganizationMemorySchemaConfig.rejected,
        (state, action) => {
          state.loading = false;
          // Set specific memory config error without affecting global error state
          state.memoryConfigError =
            action.error.message ||
            "Failed to update memory schema configuration";
        }
      );

    // Update organization functionality removed
    /*
    builder
      .addCase(updateOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateOrganization.fulfilled,
        (state, action: PayloadAction<APIOrganization>) => {
          state.loading = false;
          const updatedOrg = action.payload;
          
          // Update in the organizations array
          const index = state.organizations.findIndex(
            (org) => org.id === updatedOrg.id
          );
          if (index !== -1) {
            state.organizations[index] = updatedOrg;
          }
          
          // Update currentOrganization if it's the same one
          if (state.currentOrganization?.id === updatedOrg.id) {
            state.currentOrganization = updatedOrg;
          }
        }
      )
      .addCase(updateOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update organization";
      });
    */
  },
});

export const { clearCurrentOrganization, clearMemoryConfigError } =
  organizationSlice.actions;
export default organizationSlice.reducer;
